/**
 * IdeasBoard — quick-capture board for raw post ideas, scrapped
 * thoughts, "circle back to this" notes. Lives at
 *
 *   /portal/workspace/ideas
 *
 * Each card is a one-liner title + optional note + tags + status
 * (open / in-progress / done). Whisper dictation on every input.
 * "Promote to draft" turns an idea into a planner draft seed so the
 * editor can refine it from there.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Sparkles, ArrowRight, Lightbulb } from "lucide-react";
import { MicButton } from "../components/MicButton";
import { BriefField, useAdminToken, studioFetch } from "../components/studio/_shared";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";

const STATUSES = [
  { v: "open",        l: "Open" },
  { v: "in-progress", l: "In progress" },
  { v: "done",        l: "Done" },
];

export default function IdeasBoard() {
  const navigate = useNavigate();
  const token = useAdminToken();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");          // status filter
  const [newTitle, setNewTitle] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newTags, setNewTags] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) navigate("/admin/login", { replace: true });
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const path = filter ? `/api/workspace/ideas?status=${filter}` : "/api/workspace/ideas";
      const d = await studioFetch(path, { token });
      setIdeas(d.ideas || []);
    } catch (e) {
      if (/401|403/.test(e.message)) {
        localStorage.removeItem("hv_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(e.message);
    } finally { setLoading(false); }
  }, [token, filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newTitle.trim()) return;
    setCreating(true); setError("");
    try {
      await studioFetch("/api/workspace/ideas", {
        method: "POST", token,
        body: {
          title: newTitle.trim(),
          note: newNote.trim() || "",
          tags: newTags.split(/\s*,\s*/).filter(Boolean),
        },
      });
      setNewTitle(""); setNewNote(""); setNewTags("");
      await load();
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const patch = async (id, body) => {
    try {
      await studioFetch(`/api/workspace/ideas/${id}`, {
        method: "PATCH", token, body,
      });
      await load();
    } catch (e) { setError(e.message); }
  };

  const trash = async (id) => {
    if (!window.confirm("Move this idea to Trash? (recoverable)")) return;
    try {
      await studioFetch(`/api/workspace/ideas/${id}`, { method: "DELETE", token });
      await load();
    } catch (e) { setError(e.message); }
  };

  const promote = async (id) => {
    // "Promote to Compose" — the new primary path. Deep-link straight
    // to the Compose page with the idea title + note pre-filled in the
    // prompt textarea. The legacy "Promote to draft" Planner flow is
    // gone since Drafts page was killed Feb 2026.
    try {
      const idea = ideas.find((x) => x.id === id);
      if (!idea) throw new Error("idea not found");
      const params = new URLSearchParams();
      params.set("idea_id", id);
      params.set("title", idea.title || "");
      if (idea.note) params.set("note", idea.note);
      if (Array.isArray(idea.tags) && idea.tags.length)
        params.set("tags", idea.tags.join(","));
      // Navigate immediately. The Compose page reads the query params
      // on mount and seeds the brief textarea. We deliberately don't
      // round-trip to the backend to mark the idea "promoted" — that
      // would add 150 ms of latency for a field nothing else reads.
      window.location.href = `/portal/content-planner?${params.toString()}`;
    } catch (e) { setError(e.message); }
  };

  return (
    <ContentPlannerLayout>
      <Helmet>
        <title>Ideas — HidroVerse Content Manager</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div data-testid="ideas-board">

        {/* Quick capture row */}
        <div className="hv-card-soft mb-5" style={{ padding: 16 }}>
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={14} style={{ color: "#0E7A82" }} />
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                           color: "#0E7A82", fontWeight: 700 }}>
              Capture a new idea
            </span>
          </div>
          <BriefField label="One-line title">
            <div className="relative">
              <input type="text" value={newTitle}
                     onChange={(e) => setNewTitle(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === "Enter" && !e.shiftKey && newTitle.trim()) {
                         e.preventDefault(); create();
                       }
                     }}
                     placeholder="e.g. 'MBR vs MBBR for 30-suite lodges — quick comparison post'"
                     data-testid="idea-new-title"
                     className="hv-input w-full pr-9" />
              <div style={{ position: "absolute", right: 6, top: "50%",
                            transform: "translateY(-50%)" }}>
                <MicButton value={newTitle} onChange={setNewTitle}
                           apiToken={token} mode="append"
                           testid="idea-new-title-mic" />
              </div>
            </div>
          </BriefField>
          <BriefField label="Notes (optional)">
            <div className="relative">
              <textarea value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={2}
                        placeholder="Anything else you want to remember — references, audience, angle"
                        data-testid="idea-new-note"
                        className="hv-input w-full pr-9"
                        style={{ resize: "vertical" }} />
              <div style={{ position: "absolute", right: 6, top: 6 }}>
                <MicButton value={newNote} onChange={setNewNote}
                           apiToken={token} mode="append"
                           testid="idea-new-note-mic" />
              </div>
            </div>
          </BriefField>
          <BriefField label="Tags (comma-separated, optional)">
            <input type="text" value={newTags}
                   onChange={(e) => setNewTags(e.target.value)}
                   placeholder="e.g. MBR, MBBR, Lodge"
                   data-testid="idea-new-tags"
                   className="hv-input w-full" />
          </BriefField>
          <div className="flex justify-end">
            <button type="button" onClick={create}
                    disabled={creating || !newTitle.trim()}
                    data-testid="idea-new-submit"
                    className="hv-pill is-active">
              <Plus size={11} />
              {creating ? "Saving…" : "Capture idea"}
            </button>
          </div>
        </div>

        {/* Filter strip */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="font-mono uppercase"
                style={{ fontSize: "0.6rem", letterSpacing: "0.20em",
                         color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
            Filter:
          </span>
          <button type="button"
                  onClick={() => setFilter("")}
                  data-testid="idea-filter-all"
                  className="hv-pill"
                  style={!filter ? { background: "#0C2A40", color: "#FFFFFF" } : undefined}>
            All ({ideas.length})
          </button>
          {STATUSES.map((s) => (
            <button key={s.v} type="button"
                    onClick={() => setFilter(s.v)}
                    data-testid={`idea-filter-${s.v}`}
                    className="hv-pill"
                    style={filter === s.v ? { background: "#0C2A40", color: "#FFFFFF" } : undefined}>
              {s.l}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 px-4 py-3"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.85rem",
                        borderRadius: 10 }}
               data-testid="idea-error">{error}</div>
        )}

        {loading && (
          <div className="font-mono uppercase"
               style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                        color: "rgba(12,42,64,0.55)" }}>Loading…</div>
        )}

        {!loading && ideas.length === 0 && (
          <div className="hv-card-soft p-8 text-center" data-testid="idea-empty">
            <Lightbulb size={20} style={{ color: "#1FB6C1", margin: "0 auto" }} />
            <p className="mt-3 font-light"
               style={{ fontSize: "0.88rem", color: "rgba(12,42,64,0.65)" }}>
              No ideas yet — capture your first one above.
            </p>
          </div>
        )}

        <ul className="space-y-3" data-testid="idea-list">
          {ideas.map((idea) => (
            <IdeaCard key={idea.id} idea={idea}
                      onPatch={(b) => patch(idea.id, b)}
                      onTrash={() => trash(idea.id)}
                      onPromote={() => promote(idea.id)}
                      token={token} />
          ))}
        </ul>
      </div>
    </ContentPlannerLayout>
  );
}

function IdeaCard({ idea, onPatch, onTrash, onPromote, token }) {
  const [title, setTitle] = useState(idea.title);
  const [note, setNote] = useState(idea.note || "");
  const [tags, setTags] = useState((idea.tags || []).join(", "));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setTitle(idea.title);
    setNote(idea.note || "");
    setTags((idea.tags || []).join(", "));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea.id, idea.updated_at]);

  const save = () => {
    onPatch({
      title, note,
      tags: tags.split(/\s*,\s*/).filter(Boolean),
    });
    setDirty(false);
  };

  const statusColor = {
    open:          { bg: "rgba(31,182,193,0.15)",  fg: "#0E7A82" },
    "in-progress": { bg: "rgba(194,146,46,0.15)",  fg: "#8A6914" },
    done:          { bg: "rgba(12,42,64,0.10)",    fg: "rgba(12,42,64,0.65)" },
  }[idea.status] || { bg: "rgba(31,182,193,0.15)", fg: "#0E7A82" };

  return (
    <li className="hv-card-soft" style={{ padding: 14 }}
        data-testid={`idea-card-${idea.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="px-2 py-0.5 font-mono uppercase"
              style={{ background: statusColor.bg, color: statusColor.fg,
                       fontSize: "0.58rem", letterSpacing: "0.18em",
                       fontWeight: 700, borderRadius: 3 }}>
          {idea.status}
        </span>
        <span className="font-mono"
              style={{ fontSize: "0.6rem", color: "rgba(12,42,64,0.55)" }}>
          {new Date(idea.updated_at).toLocaleString()}
        </span>
        <div className="flex-1" />
        <select value={idea.status}
                onChange={(e) => onPatch({ status: e.target.value })}
                data-testid={`idea-status-${idea.id}`}
                className="hv-input"
                style={{ fontSize: "0.72rem", padding: "3px 6px" }}>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>

      <div className="relative">
        <input type="text" value={title}
               onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
               data-testid={`idea-title-${idea.id}`}
               className="hv-input w-full pr-9"
               style={{ fontSize: "0.95rem", fontWeight: 600 }} />
        <div style={{ position: "absolute", right: 6, top: "50%",
                      transform: "translateY(-50%)" }}>
          <MicButton value={title}
                     onChange={(v) => { setTitle(v); setDirty(true); }}
                     apiToken={token} mode="append"
                     testid={`idea-title-mic-${idea.id}`} />
        </div>
      </div>

      <div className="relative mt-2">
        <textarea value={note}
                  onChange={(e) => { setNote(e.target.value); setDirty(true); }}
                  rows={2}
                  placeholder="Notes…"
                  data-testid={`idea-note-${idea.id}`}
                  className="hv-input w-full pr-9"
                  style={{ resize: "vertical", fontSize: "0.85rem" }} />
        <div style={{ position: "absolute", right: 6, top: 6 }}>
          <MicButton value={note}
                     onChange={(v) => { setNote(v); setDirty(true); }}
                     apiToken={token} mode="append"
                     testid={`idea-note-mic-${idea.id}`} />
        </div>
      </div>

      <input type="text" value={tags}
             onChange={(e) => { setTags(e.target.value); setDirty(true); }}
             data-testid={`idea-tags-${idea.id}`}
             className="hv-input w-full mt-2"
             style={{ fontSize: "0.78rem" }}
             placeholder="tags, comma-separated" />

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {dirty && (
          <button type="button" onClick={save}
                  data-testid={`idea-save-${idea.id}`}
                  className="hv-pill is-active">
            Save
          </button>
        )}
        <div className="flex-1" />
        <button type="button" onClick={onPromote}
                data-testid={`idea-promote-${idea.id}`}
                className="hv-pill is-active"
                style={{ background: "#0E7A82", color: "#FFFFFF",
                         borderColor: "#0E7A82" }}>
          <Sparkles size={11} /> Promote to Compose <ArrowRight size={11} />
        </button>
        <button type="button" onClick={onTrash}
                data-testid={`idea-trash-${idea.id}`}
                className="hv-pill is-danger">
          <Trash2 size={11} />
        </button>
      </div>
    </li>
  );
}
